import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmLooseBarrelTypeClient, FirearmLooseBarrelTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmLooseBarrelTypeClient = () => new FirearmLooseBarrelTypeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useFirearmLooseBarrelTypes = () =>
  useQuery({
    queryKey: ['firearmLooseBarrelTypes'],
    queryFn: async () => {
      const response = await getFirearmLooseBarrelTypeClient().searchFirearmLooseBarrelTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmLooseBarrelTypeSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmLooseBarrelTypeClient().searchFirearmLooseBarrelTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmLooseBarrelTypeById = (id: string) =>
  useQuery({
    queryKey: ['firearmLooseBarrelType', id],
    queryFn: async () => {
      const response = await getFirearmLooseBarrelTypeClient().getFirearmLooseBarrelTypeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmLooseBarrelType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmLooseBarrelTypeDTO) => {
      await getFirearmLooseBarrelTypeClient().createFirearmLooseBarrelType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmLooseBarrelTypes'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmLooseBarrelType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmLooseBarrelTypeDTO }) => {
      await getFirearmLooseBarrelTypeClient().updateFirearmLooseBarrelType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmLooseBarrelTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmLooseBarrelType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmLooseBarrelTypeClient().deleteFirearmLooseBarrelType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmLooseBarrelTypes'] });
    },
  });
};